import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { employeeApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import { Card, CardContent } from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Badge from '../../components/ui/Badge.jsx';
import { Plus, Plane } from 'lucide-react';

const STATUS_VARIANT = { pending: 'warning', booked: 'success', cancelled: 'default' };

export default function MyTravelPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    employeeApi.listMyTravel()
      .then((res) => setRequests(res.requests || []))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <div className="flex items-center justify-center py-20"><div className="h-6 w-6 animate-spin rounded-full border-[2.5px] border-border border-t-primary" /></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">My Travel</h1>
          <p className="text-muted-foreground">{requests.length} request{requests.length !== 1 ? 's' : ''}</p>
        </div>
        <Button onClick={() => navigate('/travel/new')}>
          <Plus className="h-4 w-4" /> New Request
        </Button>
      </div>

      {requests.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Plane className="h-8 w-8 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No travel requests yet.</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="divide-y divide-border">
            {requests.map((r) => (
              <div
                key={r.rowKey}
                onClick={() => navigate(`/travel/${encodeURIComponent(r.rowKey)}`)}
                className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-accent/50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                    <Plane className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground">
                      {r.departureLocation} → {r.finalDestination}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {r.departureDate} to {r.arrivalDate}
                      {r.bookedAirline && ` · ${r.bookedAirline} ${r.bookedFlightNumber}`}
                    </div>
                    {r.groupId && (
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        Group trip · requested by {r.requestedByName || '—'}
                        {r.status === 'pending' && !r.passportFileName && (
                          <span className="ml-1 font-medium text-warning">
                            · your passport is still needed
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <Badge variant={STATUS_VARIANT[r.status] || 'default'}>{r.status}</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
